import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reservationApi, settlementApi } from '../../lib/api';

const STATUS_LABEL: Record<string, string> = {
  draft: '작성중', requested: '검토 대기', host_approved: '호스트 승인',
  payment_pending: '견적 확정', payment_authorized: '카드 등록 완료',
  confirmed: '예약 확정', in_progress: '대여 중', completed: '반납 완료',
  payment_captured: '정산 완료', settled: '종료',
  cancelled: '취소됨', rejected_by_host: '호스트 거절', rejected_by_admin: '관리자 거절',
};

export default function HostDashboard() {
  const [reservations, setReservations] = useState<any[]>([]);
  const [totals, setTotals] = useState({ pending: 0, settled: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      reservationApi.list({ role: 'host' }),
      settlementApi.total(),
    ]).then(([resRes, settleRes]: any[]) => {
      setReservations(resRes.reservations?.slice(0, 10) || []);
      setTotals(settleRes || { pending: 0, settled: 0 });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';
  const formatDate = (d: string) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${dt.getMonth()+1}월 ${dt.getDate()}일`;
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }

  const pendingCount = reservations.filter((r: any) => r.status === 'requested').length;
  const activeCount = reservations.filter((r: any) =>
    ['confirmed', 'in_progress', 'payment_pending', 'payment_authorized'].includes(r.status)
  ).length;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">호스트 대시보드</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <div className="card p-5">
          <p className="text-3xl font-bold text-brand-600">{pendingCount}</p>
          <p className="text-sm text-gray-500">새 예약 요청</p>
        </div>
        <div className="card p-5">
          <p className="text-3xl font-bold text-blue-600">{activeCount}</p>
          <p className="text-sm text-gray-500">진행 중</p>
        </div>
        <div className="card p-5">
          <p className="text-3xl font-bold text-green-600">{formatPrice(totals.pending)}</p>
          <p className="text-sm text-gray-500">정산 예정</p>
        </div>
        <div className="card p-5">
          <p className="text-3xl font-bold text-gray-600">{formatPrice(totals.settled)}</p>
          <p className="text-sm text-gray-500">누적 정산</p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <Link to="/host/vehicles/new" className="bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm py-2.5 rounded-xl text-center transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">+ 차량 등록</Link>
        <Link to="/host/vehicles" className="bg-white border border-gray-200 hover:bg-gray-50 text-slate-700 font-medium text-sm py-2.5 rounded-xl text-center transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">내 차량 관리</Link>
        <Link to="/host/calendar" className="bg-white border border-gray-200 hover:bg-gray-50 text-slate-700 font-medium text-sm py-2.5 rounded-xl text-center transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">캘린더</Link>
        <Link to="/host/earnings" className="bg-white border border-gray-200 hover:bg-gray-50 text-slate-700 font-medium text-sm py-2.5 rounded-xl text-center transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">수익 현황</Link>
      </div>

      {/* Recent Reservations */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">최근 예약</h2>
          <Link to="/host/vehicles" className="text-xs text-brand-600 hover:underline transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">전체 보기</Link>
        </div>
        {reservations.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center">아직 예약 요청이 없습니다</p>
        ) : (
          <div className="space-y-1.5">
            {reservations.map((r: any) => {
              const vehicleNames = r.vehicles?.length
                ? r.vehicles.map((v: any) => `${v.make} ${v.model}`).join(', ')
                : '차량 정보 없음';

              return (
                <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{vehicleNames}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(r.pickup_date)} ~ {formatDate(r.return_date)} ({r.rental_days}일)
                      <span className="mx-1">·</span>
                      {formatPrice(r.total_amount)}
                    </p>
                  </div>
                  <span className={`shrink-0 ml-3 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    r.status === 'requested' ? 'bg-amber-100 text-amber-700' :
                    r.status === 'in_progress' ? 'bg-green-100 text-green-700' :
                    r.status.includes('rejected') || r.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {STATUS_LABEL[r.status] || r.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
